import type { Logger, LoggerOptions } from '@zg-rentals/logger-base';
import { trackChildLoggers } from '@zg-rentals/logger-base';
import pino from 'pino';
import pinoTransmitHttp from 'pino-transmit-http';

export type BrowserLoggerOptions = LoggerOptions & {
  // Options documented at https://github.com/sventschui/pino-transmit-http
  transmitOptions?: Record<string, unknown>;
};

interface ErrorContainer {
  err: Error;
}

interface LogEvent {
  messages: [string | Error | ErrorContainer];
}

let activeLoggers: Record<string, Logger> = {};

const transmitters = {
  silent: () => ({
    send: () => {},
  }),
  file: (options: BrowserLoggerOptions['transmitOptions']) =>
    pinoTransmitHttp({
      throttle: 2000,
      ...options,
    }),
  console: () => ({
    send: function (level: string, logEvent: LogEvent) {
      logEvent.messages.forEach((message) => {
        let param;
        //@ts-ignore
        if (message.stack) {
          param = (message as Error).stack;
          //@ts-ignore
        } else if (message.err && message.err.stack) {
          param = (message as ErrorContainer).err.stack;
        } else {
          param = message;
        }
        // eslint-disable-next-line no-console
        console.log(level, param);
      });
    },
  }),
};

export function resetLoggers() {
  activeLoggers = {};
}

export default function getBrowserLogger({
  logPath,
  browser,
  silent,
  transmitOptions = {},
  ...options
}: BrowserLoggerOptions = {}): Logger {
  let loggerType: keyof typeof transmitters = 'console';
  if (silent || options.level === 'silent') {
    loggerType = 'silent';
  } else if (logPath) {
    loggerType = 'file';
    transmitOptions.url = logPath;
  }

  let logger = activeLoggers[loggerType];

  if (logger) {
    if (options.level && options.level !== logger.level) {
      logger.level = options.level;
    }
    return logger;
  }

  logger = pino({
    level: 'info',
    ...options,
    browser: {
      transmit: transmitters[loggerType](transmitOptions),
      ...browser,
    },
  });

  trackChildLoggers(logger);

  activeLoggers[loggerType] = logger;

  return logger;
}
